<div class="hero">
  <img src="verizon-logo.svg" alt="Verizon" class="hero-logo" />
  <h1>Network Planner</h1>
  <p class="subtitle">Geospatial planning workspace for Verizon RF design, simulation, and reporting</p>
  <p class="coming-next">From interactive maps to scenario compare dashboards, in one workflow</p>
  <div class="cta-group">
    <a routerLink="/projects" class="cta-btn">Projects</a>
    @if (projectInventoryReportUrl; as reportUrl) {
      <a [href]="reportUrl" target="_blank" rel="noopener noreferrer" class="cta-btn cta-btn-secondary">Reports</a>
    }
    <a routerLink="/vsub" class="cta-btn">Coverage Analysis →</a>
    <a routerLink="/admin/map-tiling" class="cta-btn cta-btn-secondary">Map Tiling Admin</a>
  </div>
</div>

<div class="vz-content">

  <section class="about">
    <h2>What This Is</h2>
    <p>
      A production-style RF planning workspace that combines geospatial visualization,
      scenario simulation, and reporting in one application. Current datasets include
      <strong>113,451 Verizon cell towers</strong> nationwide,
      with architecture intended for phased expansion as data volume and compute capacity grow.
    </p>
  </section>

  <section class="tech">
    <h2>Capability Stack</h2>
    <p class="tech-intro">
      The platform is organized as focused capabilities so planners can move from raw data to engineering decisions quickly.
    </p>
    <div class="tech-grid">
      <div class="tech-card">
        <p class="tech-kicker">Application</p>
        <h3 class="tech-title">
          <span class="brand-logos" aria-hidden="true">
            <img src="brand-icons/angular.svg" alt="" class="brand-logo" />
          </span>
          <span>Angular 21 Interface</span>
        </h3>
        <p>
          <strong>Angular 21</strong> provides the frontend framework for routing, stateful workflows,
          and the interactive planning workspace.
        </p>
      </div>
      <div class="tech-card">
        <p class="tech-kicker">Simulation Core</p>
        <h3 class="tech-title">
          <span class="brand-logos" aria-hidden="true">
            <img src="brand-icons/nvidia.svg" alt="" class="brand-logo" />
          </span>
          <span>NVIDIA Sionna RT</span>
        </h3>
        <p>
          A <strong>lightning-fast stand-alone ray tracer for radio propagation modeling</strong>,
          used here as the RF simulation core for coverage and what-if scenario analysis.
        </p>
      </div>
      <div class="tech-card">
        <p class="tech-kicker">Map Rendering</p>
        <h3 class="tech-title">
          <span class="brand-logos" aria-hidden="true">
            <img src="visgl-logo-dark.png" alt="" class="brand-logo brand-logo--visgl" />
          </span>
          <span>deck.gl</span>
        </h3>
        <p>
          A <strong>GPU-powered framework for visual exploratory data analysis of large datasets</strong>,
          used here for dense geospatial overlays and the main planning map.
        </p>
      </div>
      <div class="tech-card">
        <p class="tech-kicker">3D Scene Viewer</p>
        <h3 class="tech-title">
          <span class="brand-logos" aria-hidden="true">
            <img src="brand-icons/threejs.svg" alt="" class="brand-logo" />
          </span>
          <span>Three.js</span>
        </h3>
        <p>
          A separate <strong>Three.js</strong> viewer lets engineers inspect geometry,
          ray paths, and simulation artifacts in detail.
        </p>
      </div>
      <div class="tech-card">
        <p class="tech-kicker">Compute</p>
        <h3 class="tech-title">
          <span class="brand-logos" aria-hidden="true">
            <img src="brand-icons/runpod.png" alt="" class="brand-logo" />
            <img src="brand-icons/nvidia.svg" alt="" class="brand-logo" />
          </span>
          <span>CPU and GPU Workers</span>
        </h3>
        <p>
          Simulation jobs can run on <strong>CPU or GPU</strong>, with <strong>RunPod-ready</strong>
          deployment paths for heavier workloads.
        </p>
      </div>
      <div class="tech-card">
        <p class="tech-kicker">Data Platform</p>
        <h3 class="tech-title">
          <span class="brand-logos" aria-hidden="true">
            <img src="brand-icons/timescale.svg" alt="" class="brand-logo" />
            <img src="brand-icons/postgresql.svg" alt="" class="brand-logo" />
          </span>
          <span>TimescaleDB + PostGIS</span>
        </h3>
        <p>
          <strong>TimescaleDB + PostGIS</strong> store towers, sectors, scenario outputs, and
          geospatial relationships for engineering analysis.
        </p>
      </div>
      <div class="tech-card">
        <p class="tech-kicker">Tile and API Serving</p>
        <h3 class="tech-title">
          <span class="brand-logos" aria-hidden="true">
            <img src="brand-icons/rust.svg" alt="" class="brand-logo" />
          </span>
          <span>Martin</span>
        </h3>
        <p>
          <strong>Martin</strong> exposes vector tiles and geodata endpoints for the app map,
          tower overlays, and supporting services.
        </p>
      </div>
      <div class="tech-card">
        <p class="tech-kicker">Basemap and Buildings</p>
        <h3 class="tech-title">
          <span class="brand-logos" aria-hidden="true">
            <img src="brand-icons/openstreetmap.svg" alt="" class="brand-logo" />
          </span>
          <span>OpenStreetMap</span>
        </h3>
        <p>
          <strong>OpenStreetMap</strong> supplies streets, footprints, and building context for
          local basemap and 3D planning views.
        </p>
      </div>
      <div class="tech-card">
        <p class="tech-kicker">Reporting Layer</p>
        <h3 class="tech-title">
          <span class="brand-logos" aria-hidden="true">
            <img src="brand-icons/metabase.svg" alt="" class="brand-logo" />
          </span>
          <span>Metabase Dashboards</span>
        </h3>
        <p>
          <strong>Metabase</strong> provides the dashboarding and analytics layer for scenario inventory,
          compare diagnostics, and network impact review.
        </p>
      </div>
      <div class="tech-card">
        <p class="tech-kicker">Planning Workflow</p>
        <h3 class="tech-title">
          <span class="brand-logos" aria-hidden="true">
            <img src="brand-icons/angular.svg" alt="" class="brand-logo" />
            <img src="brand-icons/metabase.svg" alt="" class="brand-logo" />
          </span>
          <span>From Scenario to Decision</span>
        </h3>
        <p>
          Create revisions, run simulations, inspect deltas, and review results in one
          planning flow.
        </p>
      </div>
    </div>
  </section>

</div>
